import { canRefreshToken, endSession, getAuthHeaders } from '../auth/session';
import type { ProblemDetail } from './types';

export const API_BASE = (() => {
  const host = window.location.hostname;
  if (host === 'localhost') return 'http://localhost:8749/api/v1';
  if (host === 'mysecondbrain.rabiej.net') return 'https://api.mysecondbrain.rabiej.net/api/v1';
  return `${window.location.origin}/api/v1`;
})();

export class ApiError extends Error {
  status: number;
  problem: ProblemDetail | null;

  constructor(status: number, problem: ProblemDetail | null) {
    super(problem?.detail || problem?.title || `Request failed (${status})`);
    this.status = status;
    this.problem = problem;
  }
}

export type QueryValue = string | number | boolean | undefined | null;

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Record<string, QueryValue>;
}

function buildUrl(path: string, query?: Record<string, QueryValue>): string {
  const url = new URL(API_BASE + path);
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined && value !== null && value !== '') {
        url.searchParams.set(key, String(value));
      }
    }
  }
  return url.toString();
}

async function parseProblem(response: Response): Promise<ProblemDetail | null> {
  try {
    return (await response.json()) as ProblemDetail;
  } catch {
    return null;
  }
}

async function attempt(path: string, options: RequestOptions, forceRefresh: boolean): Promise<Response> {
  const authHeaders = await getAuthHeaders(forceRefresh);
  const headers: Record<string, string> = { Accept: 'application/json', ...authHeaders };
  const init: RequestInit = { method: options.method ?? 'GET', headers };
  if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    init.body = JSON.stringify(options.body);
  }
  return fetch(buildUrl(path, options.query), init);
}

function redirectToLogin(): void {
  void endSession().finally(() => {
    if (window.location.pathname !== '/login') {
      window.location.assign('/login');
    }
  });
}

const delay = (ms: number) => new Promise((r) => setTimeout(r, ms));

async function resolve(run: (forceRefresh: boolean) => Promise<Response>): Promise<Response> {
  let response = await run(false);

  if (response.status === 401) {
    // A 401 is not necessarily a dead session: a just-restarted backend warming its
    // JWK cache can briefly reject valid tokens. Force a token refresh, then retry a
    // couple more times with short backoff before giving up — so a transient blip
    // (deploy, cold cache) doesn't destroy a perfectly good login.
    if (!canRefreshToken()) {
      redirectToLogin();
      throw new ApiError(401, await parseProblem(response));
    }
    for (let attempt = 0; attempt < 3 && response.status === 401; attempt++) {
      if (attempt > 0) await delay(400 * attempt);
      response = await run(true);
    }
    if (response.status === 401) {
      redirectToLogin();
      throw new ApiError(401, await parseProblem(response));
    }
  }

  if (!response.ok) {
    throw new ApiError(response.status, await parseProblem(response));
  }

  return response;
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await resolve((forceRefresh) => attempt(path, options, forceRefresh));

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

export async function apiUpload<T>(path: string, formData: FormData): Promise<T> {
  const response = await resolve(async (forceRefresh) => {
    const authHeaders = await getAuthHeaders(forceRefresh);
    const headers: Record<string, string> = { Accept: 'application/json', ...authHeaders };
    return fetch(buildUrl(path), { method: 'POST', headers, body: formData });
  });

  return (await response.json()) as T;
}

export async function apiBlob(path: string): Promise<Blob> {
  const response = await resolve(async (forceRefresh) => {
    const authHeaders = await getAuthHeaders(forceRefresh);
    return fetch(buildUrl(path), { method: 'GET', headers: authHeaders });
  });

  return response.blob();
}
